//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'
import forIn from 'lodash/forIn'

import { useFetchSessionUserQuery } from 'api/sessionUser'
import {
  useFetchTicketForCreateQuery,
  useFetchTicketForCreateNewChildQuery,
  useCreateTicketMutation,
} from 'api/tickets'

import { RouteTicketingSystems, RouteTicketingSystemsTickets, RouteTicketingSystemsTicket } from 'routes'
import { getInitialValues } from 'utils/ticketFields'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import TicketForm from 'components/App/TicketEditPage/TicketForm/Index'
import TicketPageSidebar from 'components/App/TicketPage/TicketPageSidebar/Index'

const TicketCreatePage = () => {
  // Polling for avoid session ending
  useFetchSessionUserQuery({}, { pollingInterval: 60000 * 10 })

  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()

  const ticketingSystemDisplayId = params.ticketingSystemDisplayId || null
  const parentTicketId = params.ticketId

  // use 1 of 2 endpoints depending on the presence of parentTicketId in URL parameter
  const { data: dataNew, isFetching } = useFetchTicketForCreateQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
  }, { skip: parentTicketId })
  const { data: dataNewChild, isFetching: isFetchingNewChild } = useFetchTicketForCreateNewChildQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    parentTicketId: parentTicketId,
  }, { skip: !parentTicketId })
  const data = dataNew || dataNewChild

  const [createTicket, { isLoading: isCreatingTicket }] = useCreateTicketMutation()
  const createTicketParams = data?.createTicketParams || {}
  const ticketingSystemName = createTicketParams.ticketingSystemName
  const parentTicket = createTicketParams.parentTicket || null
  const withHierarchy = !!createTicketParams.hierarchyEnabled

  const onTicketCreate = (values) => {
    const paramValues = {}
    forIn(values, (value, name) => {
      if (value !== null) {
        if (value._isAMomentObject) {
          paramValues[name] = value.unix()
        } else if (withHierarchy && name === 'parent') {
          paramValues[name] = value.shortLabel || value.value
        } else {
          paramValues[name] = value
        }
      }
    })

    const params = {
      ticket: paramValues,
    }

    createTicket({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
      ticketParams: params,
    }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        const newTicketId = fulfilled.data.ticket.externalId

        // switch to the just created ticket
        navigate(RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId: newTicketId }))
      }
    })
  }

  const onCancel = () => {
    navigate(RouteTicketingSystemsTickets({ ticketingSystemDisplayId }))
  }

  const getBreadcrumbs = () => {
    const ticketingSystemsUrl = RouteTicketingSystems()
    const ticketingSystemUrl = RouteTicketingSystemsTickets({ ticketingSystemDisplayId })
    const parentTicketUrl = parentTicketId ? RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId: parentTicketId }) : null

    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketingSystems'),
      url: ticketingSystemsUrl,
    }, {
      text: ticketingSystemName,
      url: ticketingSystemUrl,
    }]

    if (parentTicketUrl) {
      breadcrumbsList.push({
        text: parentTicketId,
        url: parentTicketUrl,
      })
    }

    breadcrumbsList.push({ text: t('common.breadcrumbTicketCreate') })

    return breadcrumbsList
  }

  // canCreateTicket will be undefined if ticketing system was not found so we may avoid
  // checking the 'TS not found' message. It will show 404 page in this case anyway
  if (data && !createTicketParams.canCreateTicket) {
    return <ErrorPage404 />
  }

  const title = t('ticketCreatePage.pageHeader', { 'tsName': ticketingSystemName, interpolation: { escapeValue: false } })
  const isLoading = isFetching || isFetchingNewChild || isCreatingTicket
  const editableFields = createTicketParams.bodyFields || []
  const initialValues = getInitialValues(editableFields, parentTicket)

  return (
    <MainWrap pageTitle={title}>
      <TicketPageSidebar ticket={createTicketParams} />
      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />
        <TicketForm
          fields={editableFields}
          initialValues={initialValues}
          onSubmit={onTicketCreate}
          onCancel={onCancel}
          withHierarchy={withHierarchy}
          parentTicket={parentTicket}
        />
      </Content>
    </MainWrap>
  )
}

export default TicketCreatePage
